import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Role } from '../types/enums';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import '../App.css';

const UserDetails: React.FC = () => {
  const { email } = useParams<{ email: string }>();
  const navigate = useNavigate();
  const isNew = !email || email === 'new';
  const decodedEmail = isNew ? '' : decodeURIComponent(email);
  const [form, setForm] = useState({
    email: decodedEmail,
    password: '',
    role: Role.User as string,
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [savedRole, setSavedRole] = useState<string | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const actorIsSuper = currentUser.role === Role.SuperAdmin;
  // Only a SuperAdmin may assign SuperAdmin or change a SuperAdmin's role.
  const roleLocked = !isNew && savedRole === Role.SuperAdmin && !actorIsSuper;
  const roleOptions: string[] = actorIsSuper
    ? [Role.User, Role.Admin, Role.SuperAdmin]
    : [Role.User, Role.Admin];
  if (form.role && !roleOptions.includes(form.role)) roleOptions.push(form.role);

  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    setForm((prev) => ({ ...prev, email: decodedEmail }));
    (async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/api/users`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const rows: Array<{ email: string; role: string }> = await res.json();
        const want = decodedEmail.trim().toLowerCase();
        const row = rows.find((u) => String(u.email).trim().toLowerCase() === want);
        if (cancelled) return;
        if (!row) {
          setError('User not found');
        } else {
          setForm((prev) => ({ ...prev, email: row.email, role: row.role }));
          setSavedRole(row.role);
        }
      } catch (err: any) {
        if (!cancelled) setError('Failed to load user: ' + err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [decodedEmail, isNew]);

  const handleSave = async () => {
    if (!form.email || !form.role) {
      setError('Email and role are required');
      return;
    }
    if (isNew && !form.password) {
      setError('Password is required for new users');
      return;
    }
    try {
      if (isNew) {
        const res = await apiFetch(`${API_BASE_URL}/api/users`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: form.email.trim(),
            password: form.password,
            role: form.role,
          }),
        });
        if (!res.ok) {
          const text = await res.text();
          throw new Error(`HTTP ${res.status}: ${text.slice(0, 80)}`);
        }
        setSuccess('User created');
        navigate('/users');
      } else {
        setSaving(true);
        setError(null);
        setSuccess(null);
        const res = await apiFetch(`${API_BASE_URL}/api/users/role`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: form.email, role: form.role }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
        setSavedRole(body.role || form.role);
        setSuccess(
          body.changed === false
            ? 'Role unchanged'
            : `Role saved as ${body.role || form.role}. It takes effect the next time ${form.email} signs in.`
        );
      }
    } catch (err: any) {
      setError('Failed to save user: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px',
    fontSize: '16px',
    borderRadius: '4px',
    border: '1px solid #000000',
    marginTop: '5px',
    color: 'var(--page-text)',
    backgroundColor: 'var(--page-bg)',
  };

  return (
    <div className="page-container" style={{ padding: '20px', maxWidth: '600px', margin: '20px auto' }}>
      <h2 className="detail-heading app-header" style={{ fontSize: '28px', marginBottom: '20px' }}>
        {isNew ? 'Add New User' : 'User Details'}
      </h2>
      {error && <p className="error">{error}</p>}
      {success && <p style={{ color: '#28A745' }}>{success}</p>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
        <label className="detail-label" style={{ fontSize: '18px' }}>
          Email:
          <input
            type="email"
            value={form.email}
            disabled={!isNew}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            style={fieldStyle}
          />
        </label>
        {isNew && (
          <label className="detail-label" style={{ fontSize: '18px' }}>
            Password:
            <input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              style={fieldStyle}
            />
          </label>
        )}
        <label className="detail-label" style={{ fontSize: '18px' }}>
          Role:
          <select
            aria-label="Role"
            value={form.role}
            disabled={loading || roleLocked}
            onChange={(e) => {
              setSuccess(null);
              setForm({ ...form, role: e.target.value });
            }}
            style={fieldStyle}
          >
            {roleOptions.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        {roleLocked && (
          <p className="detail-label" style={{ fontSize: '14px' }}>
            Only a SuperAdmin can change a SuperAdmin&apos;s role.
          </p>
        )}
      </div>
      <div style={{ marginTop: '20px', textAlign: 'center' }}>
        {(isNew || !roleLocked) && (
          <button
            onClick={handleSave}
            disabled={saving || loading || (!isNew && form.role === savedRole)}
            style={{
              backgroundColor: 'var(--page-accent)',
              color: 'var(--page-accent-text)',
              padding: '10px 20px',
              border: 'none',
              borderRadius: '4px',
              fontSize: '16px',
              cursor: 'pointer',
            }}
          >
            Save
          </button>
        )}
        <button
          onClick={() => navigate('/users')}
          style={{
            backgroundColor: '#000000',
            color: 'var(--menu-accent)',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            fontSize: '16px',
            marginLeft: '10px',
            cursor: 'pointer',
          }}
        >
          Back
        </button>
      </div>
    </div>
  );
};

export default UserDetails;
